import * as signalR from "@microsoft/signalr";
import { apiUrl } from "../server";

/**
 * Сервис для real-time сообщений через ChatHub.
 * JoinCompany() — подписка на все новые сообщения компании. Событие ReceiveMessage.
 * Join(tokenCode, code) — присоединение к конкретному диалогу (legacy).
 */
class DialogSignalRService {
  constructor() {
    this.connection = null;
    this.isConnected = false;
    this.eventHandlers = {
      onReceiveMessage: null,
      onError: null,
      onConnectionStateChange: null,
    };
  }

  async _buildConnection(handlers) {
    this.eventHandlers = { ...this.eventHandlers, ...handlers };

    console.log("[DialogSignalR] Подключение к хабу...", `${apiUrl}/hubs/chat`);

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/hubs/chat`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .configureLogging(signalR.LogLevel.Error)
      .build();

    this.connection.on("ReceiveMessage", (messageDto) => {
      if (this.eventHandlers.onReceiveMessage) {
        this.eventHandlers.onReceiveMessage(messageDto);
      }
    });

    this.connection.on("Error", (err) => {
      if (this.eventHandlers.onError) {
        this.eventHandlers.onError(new Error(err));
      }
    });

    this.connection.onreconnecting(() => {
      console.log("[DialogSignalR] Переподключение...");
      this.isConnected = false;
      this.eventHandlers.onConnectionStateChange?.(false);
    });

    this.connection.onclose(() => {
      console.log("[DialogSignalR] Соединение закрыто");
      this.isConnected = false;
      this.eventHandlers.onConnectionStateChange?.(false);
    });

    this.connection.onreconnected(() => {
      console.log("[DialogSignalR] Переподключено");
      this.isConnected = true;
      this.eventHandlers.onConnectionStateChange?.(true);
    });

    await this.connection.start();
    console.log("[DialogSignalR] Хабу подключён");
    this.isConnected = true;
    this.eventHandlers.onConnectionStateChange?.(true);
  }

  /** Подключение к хабу по компании: все новые сообщения компании приходят в ReceiveMessage.
   * @param {string} sessionId — Guid сессии (из user.sessionId или localStorage).
   */
  async joinCompany(sessionId, handlers = {}) {
    try {
      console.log("[DialogSignalR] JoinCompany: попытка подключения", { sessionId: sessionId ? "***" : null });
      await this._buildConnection(handlers);
      await this.connection.invoke("JoinCompany", sessionId);
      console.log("[DialogSignalR] JoinCompany: успешно");
    } catch (error) {
      console.warn("[DialogSignalR] JoinCompany: ошибка", error);
      this.isConnected = false;
      this.eventHandlers.onConnectionStateChange?.(false);
      if (this.eventHandlers.onError) {
        this.eventHandlers.onError(error);
      }
      throw error;
    }
  }

  /** Подключение к одному диалогу по коду канала и externalId (legacy). */
  async join(channelCode, dialogExternalId, handlers = {}) {
    try {
      console.log("[DialogSignalR] Join: попытка подключения", { channelCode, dialogExternalId });
      await this._buildConnection(handlers);
      const result = await this.connection.invoke("Join", channelCode, dialogExternalId ?? "");
      console.log("[DialogSignalR] Join: успешно");
      return result;
    } catch (error) {
      console.warn("[DialogSignalR] Join: ошибка", error);
      this.isConnected = false;
      this.eventHandlers.onConnectionStateChange?.(false);
      if (this.eventHandlers.onError) {
        this.eventHandlers.onError(error);
      }
      throw error;
    }
  }

  async stop() {
    if (!this.connection) return;
    try {
      console.log("[DialogSignalR] Остановка подключения");
      this.isConnected = false;
      this.eventHandlers.onConnectionStateChange?.(false);
      await this.connection.stop();
      this.connection = null;
    } catch (error) {
      console.error("[DialogSignalR] Ошибка при остановке:", error);
    }
  }
}

export default new DialogSignalRService();
